// tabs/SettingsTab.js
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  MenuItem,
  InputAdornment,
  Tooltip,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Info as InfoIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  LocalOffer as TagIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { API_BASE_URL } from 'config/api';
import {
  SectionCard,
  SectionTitle,
  FormGrid,
  StyledTextField,
  StyledSelect,
  ActionButton,
  DangerButton,
  honeyColors,
} from '../styled';

const SettingsTab = ({
  formData,
  availableTags,
  saving,
  hasChanges,
  onFormChange,
  onSave,
  onCancel,
  onImageUpload,
  onTagAdd,
  onTagRemove,
}) => {
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'object') return URL.createObjectURL(image);
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'link'
  ];

  return (
    <Box>
      {/* Save Actions Bar */}
      {hasChanges && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: honeyColors.surfaceHoney,
            padding: 2,
            borderRadius: '12px',
            mb: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: `2px solid ${honeyColors.primary}`,
          }}
        >
          <Typography sx={{ fontWeight: 600, color: honeyColors.textPrimary }}>
            You have unsaved changes
          </Typography>
          <Box display="flex" gap={2}>
            <ActionButton variant="outlined" onClick={onCancel} disabled={saving}>
              Discard
            </ActionButton>
            <ActionButton
              variant="contained"
              onClick={onSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </ActionButton>
          </Box>
        </Box>
      )}

      {/* Basic Information */}
      <SectionCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Basic Information
          </SectionTitle>

          <FormGrid>
            <StyledTextField
              label="Community Name"
              value={formData.name || ''}
              onChange={(e) => onFormChange('name', e.target.value)}
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="The display name for your community">
                      <HelpIcon sx={{ color: honeyColors.textMuted }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <StyledTextField
              label="Community Slug"
              value={formData.slug || ''}
              disabled
              fullWidth
              helperText="URL identifier (cannot be changed)"
            />
          </FormGrid>

          <Box sx={{ mb: 3 }}>
            <StyledTextField
              label="Description"
              value={formData.description || ''}
              onChange={(e) => onFormChange('description', e.target.value)}
              multiline
              rows={3}
              fullWidth
              helperText="Brief description shown in community listings"
            />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Promotional Text
              </Typography>
              <Box
                sx={{
                  border: `2px solid ${honeyColors.border}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  '& .ql-container': { minHeight: '150px' },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={formData.promotional_text || ''}
                  onChange={(content) => onFormChange('promotional_text', content)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter promotional content..."
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Community Rules
              </Typography>
              <Box
                sx={{
                  border: `2px solid ${honeyColors.border}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  '& .ql-container': { minHeight: '150px' },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={formData.rules || ''}
                  onChange={(content) => onFormChange('rules', content)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter community rules..."
                />
              </Box>
            </Grid>
          </Grid>

          <FormGrid sx={{ mt: 3 }}>
            <StyledTextField
              label="Contact Email"
              type="email"
              value={formData.contact_email || ''}
              onChange={(e) => onFormChange('contact_email', e.target.value)}
              fullWidth
            />

            <StyledTextField
              label="Space Limit"
              type="number"
              value={formData.space_limit || 0}
              onChange={(e) => onFormChange('space_limit', parseInt(e.target.value) || 0)}
              fullWidth
              helperText="0 = unlimited"
              inputProps={{ min: 0 }}
            />
          </FormGrid>
        </CardContent>
      </SectionCard>

      {/* Status & Visibility */}
      <SectionCard>
        <CardContent>
          <SectionTitle>
            <SecurityIcon />
            Status & Visibility
          </SectionTitle>

          <FormGrid>
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Community Status
              </Typography>
              <StyledSelect
                value={formData.status || 'PP'}
                onChange={(e) => onFormChange('status', e.target.value)}
                fullWidth
              >
                <MenuItem value="AC">Active</MenuItem>
                <MenuItem value="PP">Pending</MenuItem>
                <MenuItem value="IN">Inactive</MenuItem>
                <MenuItem value="RE">Rejected</MenuItem>
                <MenuItem value="BA">Banned</MenuItem>
              </StyledSelect>
            </Box>

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Community Visibility
              </Typography>
              <StyledSelect
                value={formData.visibility || 'PU'}
                onChange={(e) => onFormChange('visibility', e.target.value)}
                fullWidth
              >
                <MenuItem value="PU">Public - Anyone can find and join</MenuItem>
                <MenuItem value="PR">Private - Visible but requires approval</MenuItem>
                <MenuItem value="SE">Secret - Only visible to members</MenuItem>
              </StyledSelect>
            </Box>

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Default Member Status
              </Typography>
              <StyledSelect
                value={formData.default_status || 'PP'}
                onChange={(e) => onFormChange('default_status', e.target.value)}
                fullWidth
              >
                <MenuItem value="AC">Active (auto-approve)</MenuItem>
                <MenuItem value="PP">Pending (require approval)</MenuItem>
                <MenuItem value="IN">Inactive</MenuItem>
              </StyledSelect>
            </Box>

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 600 }}>
                Default Member Role
              </Typography>
              <StyledSelect
                value={formData.default_role || 'PA'}
                onChange={(e) => onFormChange('default_role', e.target.value)}
                fullWidth
              >
                <MenuItem value="PA">Participant</MenuItem>
                <MenuItem value="GU">Guest</MenuItem>
                <MenuItem value="MA">Manager</MenuItem>
                <MenuItem value="AD">Admin</MenuItem>
              </StyledSelect>
            </Box>
          </FormGrid>
        </CardContent>
      </SectionCard>

      {/* Tags */}
      <SectionCard>
        <CardContent>
          <SectionTitle>
            <TagIcon />
            Community Tags
          </SectionTitle>

          <Autocomplete
            options={availableTags.filter(tag => 
              !formData.event_tags?.some(t => t.id === tag.id)
            )}
            getOptionLabel={(option) => option.name || ''}
            onChange={(event, value) => {
              if (value) onTagAdd(value);
            }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label="Add Tags"
                placeholder="Search and add tags..."
              />
            )}
            sx={{ mb: 2 }}
          />

          <Box display="flex" flexWrap="wrap" gap={1}>
            {formData.event_tags?.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.name}
                onDelete={() => onTagRemove(tag.id)}
                sx={{
                  backgroundColor: honeyColors.surfaceHoney,
                  borderColor: honeyColors.primary,
                  '& .MuiChip-deleteIcon': {
                    color: honeyColors.textSecondary,
                    '&:hover': { color: honeyColors.error },
                  },
                }}
              />
            ))}
            {(!formData.event_tags || formData.event_tags.length === 0) && (
              <Typography color="textSecondary">No tags added yet</Typography>
            )}
          </Box>
        </CardContent>
      </SectionCard>

      {/* Images */}
      <SectionCard>
        <CardContent>
          <SectionTitle>
            <VisibilityIcon />
            Community Images
          </SectionTitle>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Community Logo
              </Typography>
              
              {formData.image ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: 200,
                    height: 200,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: `2px solid ${honeyColors.border}`,
                    mb: 2,
                  }}
                >
                  <img
                    src={getImageUrl(formData.image)}
                    alt="Community logo"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ) : (
                <Box
                  onClick={() => onImageUpload('image')}
                  sx={{
                    width: 200,
                    height: 200,
                    borderRadius: '16px',
                    border: `2px dashed ${honeyColors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: honeyColors.surfaceHoney,
                    mb: 2,
                    '&:hover': {
                      borderColor: honeyColors.primary,
                    },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: honeyColors.textMuted, mb: 1 }} />
                  <Typography color="textSecondary">Upload Logo</Typography>
                  <Typography variant="caption" color="textSecondary">Max 5MB</Typography>
                </Box>
              )}
              
              <ActionButton
                variant="outlined"
                onClick={() => onImageUpload('image')}
                startIcon={<CloudUploadIcon />}
              >
                {formData.image ? 'Change Logo' : 'Upload Logo'}
              </ActionButton>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Background Image
              </Typography>
              
              {formData.background ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 150,
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: `2px solid ${honeyColors.border}`,
                    mb: 2,
                  }}
                >
                  <img
                    src={getImageUrl(formData.background)}
                    alt="Background"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ) : (
                <Box
                  onClick={() => onImageUpload('background')}
                  sx={{
                    width: '100%',
                    height: 150,
                    borderRadius: '16px',
                    border: `2px dashed ${honeyColors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: honeyColors.surfaceHoney,
                    mb: 2,
                    '&:hover': {
                      borderColor: honeyColors.primary,
                    },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: honeyColors.textMuted, mb: 1 }} />
                  <Typography color="textSecondary">Upload Background</Typography>
                  <Typography variant="caption" color="textSecondary">1920x280px recommended, Max 10MB</Typography>
                </Box>
              )}
              
              <ActionButton
                variant="outlined"
                onClick={() => onImageUpload('background')}
                startIcon={<CloudUploadIcon />}
              >
                {formData.background ? 'Change Background' : 'Upload Background'}
              </ActionButton>
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>
    </Box>
  );
};

export default SettingsTab;
